import { useCallback, useEffect, useRef, useState } from "react";
import QRScanner from "../../compoment/features/QRScanner";
import { Info, ScanBack } from "../../compoment/icon";
import qrTicketIcon from "../../assets/img/qr-ticket.png";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyTicketStatusApi } from "../../api";
import SuccessScanModal from "../../compoment/modals/SuccessScanModal";
import FailureScanModal from "../../compoment/modals/FailureScanModal";
import Loader from "../../compoment/ui/Loader";
import ExpiredModal from "../../modal/ExpiredModal";
import { useSocket } from "../../context/SocketContext";
import {
  buildTicketDetailFromScanResponse,
  CAMERA_ACCESS_DENIED_MESSAGE,
  DEFAULT_EXPIRED_TICKET_MESSAGE,
  DEFAULT_INVALID_MESSAGE,
  DEFAULT_SUCCESS_MESSAGE,
  getExtendTicketUrl,
  getTicketIdFromExtensionEvent,
  getTicketIdFromScan,
  isObjectExtensionPayload,
  isTicketExtendable,
  ScanTicketResponse,
  setTicketDetailInStorage,
  TICKET_EXTENDED_MESSAGE,
} from "../../utils/ticket";

const QRScanScreen = () => {
  const [hasScannedError, setHasScannedError] = useState(false);
  const [nonRenewableExpiredModal, setNonRenewableExpiredModal] = useState(false);
  const [successScan, setSuccessScan] = useState(false);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS_MESSAGE);
  const [expiredModal, setExpiredModal] = useState(false);
  const [expiredTicketId, setExpiredTicketId] = useState("");
  const [expiredMessage, setExpiredMessage] = useState(
    DEFAULT_EXPIRED_TICKET_MESSAGE
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showScanner, setShowScanner] = useState(true);
  const [scannerRestart, setScannerRestart] = useState(0);
  const navigate = useNavigate();
  const location = useLocation() as { state?: { rescan?: number } };
  const { paymentSocket, paymentConnected } = useSocket();
  const expiredModalRef = useRef(expiredModal);
  const expiredTicketIdRef = useRef(expiredTicketId);
  const navigatingAfterExtensionRef = useRef(false);

  useEffect(() => {
    if (!location.state?.rescan) return;

    setExpiredModal(false);
    setHasScannedError(false);
    setNonRenewableExpiredModal(false);
    setScannerRestart((count) => count + 1);
    setShowScanner(false);

    const timer = setTimeout(() => {
      setShowScanner(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [location.state?.rescan]);

  useEffect(() => {
    expiredModalRef.current = expiredModal;
    expiredTicketIdRef.current = expiredTicketId;
  }, [expiredModal, expiredTicketId]);

  const handleScanSuccess = async (decodedText: string) => {
    const ticketId = getTicketIdFromScan(decodedText);
    if (!ticketId) return;

    setIsLoading(true);

    try {
      const response = await verifyTicketStatusApi({ ticketId });
      const data = (response.data || {}) as ScanTicketResponse;
      const ticket = data.ticket;
      const ticketDetail = buildTicketDetailFromScanResponse(data);

      if (data.isExpired || ticket?.ticket_status?.toLowerCase() === "expired") {
        const message = data.message || DEFAULT_EXPIRED_TICKET_MESSAGE;

        if (isTicketExtendable(data)) {
          setExpiredTicketId(ticket?.id || ticketId);
          setExpiredMessage(message);
          if (ticketDetail) {
            setTicketDetailInStorage(ticketDetail);
          }
          setExpiredModal(true);
        } else {
          setExpiredMessage(message);
          setNonRenewableExpiredModal(true);
        }
        return;
      }

      if (data.validate === true && ticket?.id && ticketDetail) {
        setSuccessMessage(DEFAULT_SUCCESS_MESSAGE);
        setTicketDetailInStorage(ticketDetail);
        setSuccessScan(true);
        setTimeout(() => {
          setSuccessScan(false);
          navigate("/main", { replace: true, state: { ticket: ticketDetail } });
        }, 2000);
        return;
      }

      const message = data.message || DEFAULT_INVALID_MESSAGE;
      setExpiredMessage(message);
      setHasScannedError(true);
    } catch (err: unknown) {
      const errorData = ((err as { response?: { data?: ScanTicketResponse } })
        ?.response?.data || {}) as ScanTicketResponse;
      const errorMessage = errorData?.message || "";
      const isExpiredTicket =
        typeof errorMessage === "string" &&
        errorMessage.toLowerCase().includes("expired");

      if (isExpiredTicket) {
        const message = errorMessage || DEFAULT_EXPIRED_TICKET_MESSAGE;

        if (isTicketExtendable(errorData)) {
          setExpiredTicketId(ticketId);
          setExpiredMessage(message);
          setExpiredModal(true);
        } else {
          setExpiredMessage(message);
          setNonRenewableExpiredModal(true);
        }
      } else {
        const message = errorMessage || DEFAULT_INVALID_MESSAGE;
        setExpiredMessage(message);
        setHasScannedError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanFailure = useCallback((error: string) => {
    setExpiredMessage(error || CAMERA_ACCESS_DENIED_MESSAGE);
    setHasScannedError(true);
  }, []);

  const restartScanner = () => {
    setScannerRestart((count) => count + 1);
  };

  const continueAfterTicketExtension = useCallback(
    async (ticketId: string) => {
      if (!ticketId || navigatingAfterExtensionRef.current) return;

      navigatingAfterExtensionRef.current = true;
      setExpiredModal(false);
      setIsLoading(true);

      try {
        const response = await verifyTicketStatusApi({ ticketId });
        const data = (response.data || {}) as ScanTicketResponse;
        const ticketDetail = buildTicketDetailFromScanResponse(data);
        if (ticketDetail) {
          setTicketDetailInStorage(ticketDetail);
        }
      } catch (error) {
        console.error("Unable to refresh extended ticket:", error);
      } finally {
        setIsLoading(false);
        setSuccessMessage(TICKET_EXTENDED_MESSAGE);
        setSuccessScan(true);
        setTimeout(() => {
          setSuccessScan(false);
          navigate("/", { replace: true });
        }, 2000);
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!paymentSocket || !paymentConnected) return;

    const handleTicketTimeUpdated = (...args: unknown[]) => {
      const payloads = args.filter(isObjectExtensionPayload);
      const eventTicketId = payloads
        .map(getTicketIdFromExtensionEvent)
        .find(Boolean);
      const scannedTicketId = expiredTicketIdRef.current;

      if (
        expiredModalRef.current &&
        scannedTicketId &&
        (!eventTicketId || eventTicketId === scannedTicketId)
      ) {
        continueAfterTicketExtension(scannedTicketId);
      }
    };

    paymentSocket.on("ticketTimeUpdated", handleTicketTimeUpdated);

    return () => {
      paymentSocket.off("ticketTimeUpdated", handleTicketTimeUpdated);
    };
  }, [continueAfterTicketExtension, paymentConnected, paymentSocket]);

  return (
    <>
      <div className="fullScreenBg" aria-hidden="true" />
      {isLoading && <Loader />}
      <section className="scanScreen">
        <button
          type="button"
          className="scanScreen__back"
          aria-label="Back"
          onClick={() => navigate("/", { replace: true })}
        >
          <ScanBack />
        </button>

        <div className="scanScreen__hint">
          <span>
            <Info />
          </span>
          Please make sure the QR Code is clearly visible to the camera
        </div>

        <div className="scanScreen__camera">
          <QRScanner
            onScanSuccess={handleScanSuccess}
            onScanFailure={handleScanFailure}
            hasScannedError={hasScannedError}
            setHasScannedError={setHasScannedError}
            showScanner={showScanner}
            restartTrigger={scannerRestart}
          />
          <div className="scanScreen__frame" aria-hidden="true">
            <span className="scanScreen__corner scanScreen__corner--tl" />
            <span className="scanScreen__corner scanScreen__corner--tr" />
            <span className="scanScreen__corner scanScreen__corner--bl" />
            <span className="scanScreen__corner scanScreen__corner--br" />
          </div>
        </div>

        <div className="scanScreen__card">
          <span className="scanScreen__cardIcon">
            <img src={qrTicketIcon} alt="" />
          </span>
          <div className="scanScreen__cardText">
            <p className="scanScreen__cardTitle">Show your QR ticket</p>
            <p className="scanScreen__cardSub">
              Hold your QR ticket steady in front of the camera.
            </p>
          </div>
        </div>
      </section>

      {successScan && (
        <SuccessScanModal
          show={successScan}
          message={successMessage}
          onHide={() => setSuccessScan(false)}
        />
      )}

      {nonRenewableExpiredModal && (
        <FailureScanModal
          show={nonRenewableExpiredModal}
          message={expiredMessage}
          redirectHomeAfterMs={5000}
          onHide={() => setNonRenewableExpiredModal(false)}
        />
      )}

      {hasScannedError && (
        <FailureScanModal
          show={hasScannedError}
          message={expiredMessage}
          redirectHomeAfterMs={5000}
          onHide={() => setHasScannedError(false)}
        />
      )}

      {expiredModal && (
        <ExpiredModal
          show={expiredModal}
          message={expiredMessage}
          extendTicketUrl={getExtendTicketUrl(expiredTicketId)}
          onHide={() => {
            setExpiredModal(false);
            setHasScannedError(false);
            restartScanner();
          }}
        />
      )}
    </>
  );
};

export default QRScanScreen;
